import { signInWithRedirect } from 'aws-amplify/auth';

import { reconfigureCognitoDomain } from '@/state/auth';
import { validateRedirectUrl } from '@/utils/redirect.util';

type SsoProvider = NonNullable<
  Parameters<typeof signInWithRedirect>[0]
>['provider'];

/**
 * Validates + persists the post-login redirect, then hands off to Cognito.
 * The validate-then-store step defends against open redirect (CWE-601); the
 * stored value is consumed during post-OAuth bootstrap in state/auth.tsx.
 * Every SSO entry point must go through here rather than calling
 * signInWithRedirect directly.
 *
 * cognitoDomain is the exact Cognito OAuth host to use for this redirect
 * (returned by /auth/sso-provider for custom SSO). It's persisted so the
 * /hello callback's token exchange runs on the same host that issued the code
 * (see initAmplify). Undefined keeps the stack's default Cognito domain.
 */
export async function startSsoSignIn(
  provider: SsoProvider,
  redirect: string | null,
  cognitoDomain?: string
) {
  const validated = validateRedirectUrl(redirect);
  if (validated !== '/') {
    sessionStorage.setItem('oauth_redirect_url', validated);
  }

  if (cognitoDomain) {
    sessionStorage.setItem('oauth_cognito_domain', cognitoDomain);
  } else {
    sessionStorage.removeItem('oauth_cognito_domain');
  }
  reconfigureCognitoDomain(cognitoDomain);

  await signInWithRedirect({ provider });
}
